import { PrinterProfile, AdvancedTemplate, SectionAlignment, SectionSize, TextWeight, TextDensity } from '../types/printerProfile';
import { TicketData } from '../types/printerProfile';
import { groupItemsByCategory } from './categoryGrouper';
import { aggregateCustomizationOptions } from './ticketFormatter';
import { formatOrderNumber } from './orderNumberFormatter';

const alignText = (text: string, alignment: SectionAlignment, width: number, centerOffset: number = 0): string => {
  const textLength = text.length;

  if (alignment === 'center') {
    const basePadding = Math.max(0, Math.floor((width - textLength) / 2));
    const adjustedPadding = Math.max(0, basePadding + centerOffset);
    return ' '.repeat(adjustedPadding) + text;
  } else if (alignment === 'right') {
    const padding = Math.max(0, width - textLength);
    return ' '.repeat(padding) + text;
  }

  return text;
};

const getEscPosSize = (size: SectionSize, useEscPos: boolean): string => {
  if (!useEscPos) return '';

  switch (size) {
    case 'large':
      return '\x1B\x21\x18';
    case 'medium':
      return '\x1B\x21\x08';
    case 'small':
    default:
      return '\x1B\x21\x00';
  }
};

const getTextWeightCommands = (weight: TextWeight, useEscPos: boolean): { start: string; end: string } => {
  if (!useEscPos) return { start: '', end: '' };

  switch (weight) {
    case 'bold':
      return {
        start: '\x1B\x45\x01',
        end: '\x1B\x45\x00'
      };
    case 'extra-bold':
      return {
        start: '\x1B\x21\x30',
        end: '\x1B\x21\x00'
      };
    case 'double-strike':
      return {
        start: '\x1B\x47\x01',
        end: '\x1B\x47\x00'
      };
    case 'bold-emphasized':
      return {
        start: '\x1B\x45\x01\x1B\x47\x01',
        end: '\x1B\x45\x00\x1B\x47\x00'
      };
    case 'underline-bold':
      return {
        start: '\x1B\x2D\x02\x1B\x45\x01',
        end: '\x1B\x2D\x00\x1B\x45\x00'
      };
    case 'normal':
    default:
      return { start: '', end: '' };
  }
};

const getTextDensityCommand = (density: TextDensity, useEscPos: boolean): string => {
  if (!useEscPos) return '';

  switch (density) {
    case 'extra-dark':
      return '\x1D\x7C\x10';
    case 'dark':
      return '\x1D\x7C\x08';
    case 'light':
      return '\x1D\x7C\x02';
    case 'normal':
    default:
      return '\x1D\x7C\x00';
  }
};

const getBoldCommand = (bold: boolean, useEscPos: boolean): string => {
  if (!useEscPos || !bold) return '';
  return '\x1B\x45\x01';
};

const getUnboldCommand = (useEscPos: boolean): string => {
  if (!useEscPos) return '';
  return '\x1B\x45\x00';
};

export const buildAdvancedTicket = (data: TicketData, profile: PrinterProfile): string => {
  const config = profile.config;
  const template = config.advancedTemplate;

  if (!template) {
    throw new Error('Advanced template not configured');
  }

  if (!template.headerLines) {
    template.headerLines = {
      restaurantName: {
        enabled: true,
        useApiName: true,
        alignment: 'center',
        size: 'large',
        bold: true,
        textWeight: 'bold',
        textDensity: 'normal',
        centerOffset: 0,
      },
      customLines: [],
    };
  }

  if (!template.headerLines.restaurantName) {
    template.headerLines.restaurantName = {
      enabled: true,
      useApiName: true,
      alignment: 'center',
      size: 'large',
      bold: true,
      textWeight: 'bold',
      textDensity: 'normal',
      centerOffset: 0,
    };
  }

  const width = config.width || 42;
  const useEscPos = config.useEscPos || false;
  const lines: string[] = [];

  const safeTranslate = (key: string, params?: any): string => {
    if (typeof data.t === 'function') {
      try {
        return data.t(key, params);
      } catch {
        return key;
      }
    }
    return key;
  };

  if (useEscPos) {
    lines.push('\x1B\x40');

    if (template.globalSettings?.enhanceBlackness) {
      const defaultDensity = template.globalSettings.defaultTextDensity || 'normal';
      lines.push(getTextDensityCommand(defaultDensity, useEscPos));
    }
  }

  if (template.headerLines.restaurantName.enabled) {
    const useApiName = template.headerLines.restaurantName.useApiName;
    let name: string;

    if (useApiName === false) {
      name = template.headerLines.restaurantName.text || 'Restaurant';
    } else {
      name = data.storeInfo?.name || template.headerLines.restaurantName.text || 'Restaurant';
    }

    const size = template.headerLines.restaurantName.size;
    const alignment = template.headerLines.restaurantName.alignment;
    const textWeight = template.headerLines.restaurantName.textWeight || 'normal';
    const textDensity = template.headerLines.restaurantName.textDensity || 'normal';
    const centerOffset = template.headerLines.restaurantName.centerOffset || 0;

    if (useEscPos) {
      lines.push(getTextDensityCommand(textDensity, useEscPos));
      lines.push(getEscPosSize(size, useEscPos));
      const weightCommands = getTextWeightCommands(textWeight, useEscPos);
      if (weightCommands.start) lines.push(weightCommands.start);
    }

    lines.push(alignText(name, alignment, width, centerOffset));

    if (useEscPos) {
      const weightCommands = getTextWeightCommands(textWeight, useEscPos);
      if (weightCommands.end) lines.push(weightCommands.end);
      lines.push(getEscPosSize('small', useEscPos));

      if (template.globalSettings?.enhanceBlackness) {
        const defaultDensity = template.globalSettings.defaultTextDensity || 'normal';
        lines.push(getTextDensityCommand(defaultDensity, useEscPos));
      }
    }
  }

  if (template.headerLines.address?.enabled && template.headerLines.address.text) {
    const textWeight = template.headerLines.address.textWeight || 'normal';
    const centerOffset = template.headerLines.address.centerOffset || 0;

    if (useEscPos) {
      const weightCommands = getTextWeightCommands(textWeight, useEscPos);
      if (weightCommands.start) lines.push(weightCommands.start);
    }

    lines.push(alignText(template.headerLines.address.text, template.headerLines.address.alignment, width, centerOffset));

    if (useEscPos) {
      const weightCommands = getTextWeightCommands(textWeight, useEscPos);
      if (weightCommands.end) lines.push(weightCommands.end);
    }
  }

  if (template.headerLines.phone?.enabled && template.headerLines.phone.text) {
    const textWeight = template.headerLines.phone.textWeight || 'normal';
    const centerOffset = template.headerLines.phone.centerOffset || 0;

    if (useEscPos) {
      const weightCommands = getTextWeightCommands(textWeight, useEscPos);
      if (weightCommands.start) lines.push(weightCommands.start);
    }

    lines.push(alignText(template.headerLines.phone.text, template.headerLines.phone.alignment, width, centerOffset));

    if (useEscPos) {
      const weightCommands = getTextWeightCommands(textWeight, useEscPos);
      if (weightCommands.end) lines.push(weightCommands.end);
    }
  }

  template.headerLines.customLines.forEach((line) => {
    if (line.text) {
      const textWeight = line.textWeight || 'normal';
      const textDensity = line.textDensity || 'normal';
      const centerOffset = line.centerOffset || 0;

      if (useEscPos) {
        if (line.textDensity) {
          lines.push(getTextDensityCommand(textDensity, useEscPos));
        }
        const weightCommands = getTextWeightCommands(textWeight, useEscPos);
        if (weightCommands.start) lines.push(weightCommands.start);
      }

      lines.push(alignText(line.text, line.alignment, width, centerOffset));

      if (useEscPos) {
        const weightCommands = getTextWeightCommands(textWeight, useEscPos);
        if (weightCommands.end) lines.push(weightCommands.end);

        if (line.textDensity && template.globalSettings?.enhanceBlackness) {
          const defaultDensity = template.globalSettings.defaultTextDensity || 'normal';
          lines.push(getTextDensityCommand(defaultDensity, useEscPos));
        }
      }
    }
  });

  if (template.separators.afterHeader) {
    lines.push(template.separators.afterHeader.substring(0, width));
  }

  lines.push(alignText(`${safeTranslate('Commande numéro')}: ${formatOrderNumber(data.orderCode)}`, 'center', width));
  lines.push(alignText(new Date().toLocaleString('fr-FR'), 'center', width));

  if (data.orderType === 'dine-in' && data.tableNumber) {
    lines.push(alignText(`${safeTranslate('Table')}: ${data.tableNumber}`, 'center', width));
  }

  if (data.customerName) {
    lines.push(alignText(`${safeTranslate('Client')}: ${data.customerName}`, 'center', width));
  }

  if (template.separators.beforeItems) {
    lines.push(template.separators.beforeItems.substring(0, width));
  }

  const showCategories = config.showCategories !== false && data.taxSettings?.showCategories !== false;
  const aggregateOptions = config.aggregateOptions !== false;
  const hasItems = data.items && Array.isArray(data.items) && data.items.length > 0;

  if (!hasItems) {
    lines.push('');
    lines.push(alignText('AUCUN ARTICLE', 'center', width));
    lines.push('');
  } else if (showCategories && data.categories && data.categories.length > 0) {
    const groupedItems = groupItemsByCategory(data.items, data.categories);

    groupedItems.forEach((group, groupIndex) => {
      if (useEscPos) {
        lines.push(getBoldCommand(true, useEscPos));
      }
      lines.push(`\n${group.categoryName}`.toUpperCase());
      if (useEscPos) {
        lines.push(getUnboldCommand(useEscPos));
      }
      lines.push('-'.repeat(width));

      group.items.forEach((item: any) => {
        const itemName = item.menuItem?.name || item.name || 'Produit';
        const itemQuantity = item.quantity || 1;
        const itemPrice = item.menuItem?.price || item.price || 0;
        const totalPrice = itemPrice * itemQuantity;

        const itemLine = `${itemQuantity}x ${itemName}`;
        const priceLine = `${totalPrice.toFixed(2)}€`;
        const padding = Math.max(1, width - itemLine.length - priceLine.length);
        lines.push(itemLine + ' '.repeat(padding) + priceLine);

        if (item.customizations && Object.keys(item.customizations).length > 0) {
          Object.values(item.customizations).forEach((options: any) => {
            if (Array.isArray(options)) {
              if (aggregateOptions) {
                const aggregated = aggregateCustomizationOptions(options);
                aggregated.forEach((opt: any) => {
                  const optText = opt.quantity > 1 ? `  ${opt.quantity}x ${opt.name}` : `  + ${opt.name}`;
                  const optPrice = opt.price > 0 ? `+${opt.price.toFixed(2)}€` : '';
                  const optPadding = Math.max(1, width - optText.length - optPrice.length);
                  lines.push(optText + ' '.repeat(optPadding) + optPrice);
                });
              } else {
                options.forEach((opt: any) => {
                  const optText = `  + ${opt.name}`;
                  const optPrice = opt.price > 0 ? `+${opt.price.toFixed(2)}€` : '';
                  const optPadding = Math.max(1, width - optText.length - optPrice.length);
                  lines.push(optText + ' '.repeat(optPadding) + optPrice);
                });
              }
            }
          });
        }
      });

      if (groupIndex < groupedItems.length - 1) {
        lines.push('');
      }
    });
  } else {
    data.items.forEach((item: any, itemIndex: number) => {
      if (!item) return;

      const itemName = item.menuItem?.name || item.name || 'Produit';
      const itemQuantity = item.quantity || 1;
      const itemPrice = item.menuItem?.price || item.price || 0;
      const totalPrice = itemPrice * itemQuantity;

      const itemLine = `${itemQuantity}x ${itemName}`;
      const priceLine = `${totalPrice.toFixed(2)}€`;
      const padding = Math.max(1, width - itemLine.length - priceLine.length);
      lines.push(itemLine + ' '.repeat(padding) + priceLine);

      if (item.customizations && Object.keys(item.customizations).length > 0) {
        Object.values(item.customizations).forEach((options: any) => {
          if (Array.isArray(options)) {
            if (aggregateOptions) {
              const aggregated = aggregateCustomizationOptions(options);
              aggregated.forEach((opt: any) => {
                const optText = opt.quantity > 1 ? `  ${opt.quantity}x ${opt.name}` : `  + ${opt.name}`;
                const optPrice = opt.price > 0 ? `+${opt.price.toFixed(2)}€` : '';
                const optPadding = Math.max(1, width - optText.length - optPrice.length);
                lines.push(optText + ' '.repeat(optPadding) + optPrice);
              });
            } else {
              options.forEach((opt: any) => {
                const optText = `  + ${opt.name}`;
                const optPrice = opt.price > 0 ? `+${opt.price.toFixed(2)}€` : '';
                const optPadding = Math.max(1, width - optText.length - optPrice.length);
                lines.push(optText + ' '.repeat(optPadding) + optPrice);
              });
            }
          }
        });
      }

      if (itemIndex < data.items.length - 1) {
        lines.push('');
      }
    });
  }

  if (template.separators.afterItems) {
    lines.push(template.separators.afterItems.substring(0, width));
  }

  if (template.separators.beforeTotals) {
    lines.push(template.separators.beforeTotals.substring(0, width));
  }

  const subtotalText = safeTranslate('Sous-total HT');
  const subtotalValue = `${data.orderTotals.subtotal.toFixed(2)}€`;
  const subtotalPadding = Math.max(1, width - subtotalText.length - subtotalValue.length);
  lines.push(subtotalText + ' '.repeat(subtotalPadding) + subtotalValue);

  if (config.showTaxDetails && data.taxResult) {
    if (Array.isArray(data.taxResult)) {
      data.taxResult.forEach((tax: any) => {
        const taxText = safeTranslate('TVA', { rate: tax.rate });
        const taxValue = `${tax.amount.toFixed(2)}€`;
        const taxPadding = Math.max(1, width - taxText.length - taxValue.length);
        lines.push(taxText + ' '.repeat(taxPadding) + taxValue);
      });
    }
  }

  if (template.separators.afterTotals) {
    lines.push(template.separators.afterTotals.substring(0, width));
  }

  if (useEscPos) {
    lines.push(getEscPosSize('medium', useEscPos));
    lines.push(getBoldCommand(true, useEscPos));
  }

  const totalText = safeTranslate('Total TTC');
  const totalValue = `${data.orderTotals.invoiceTotal.toFixed(2)}€`;
  const totalPadding = Math.max(1, width - totalText.length - totalValue.length);
  lines.push(totalText + ' '.repeat(totalPadding) + totalValue);

  if (useEscPos) {
    lines.push(getUnboldCommand(useEscPos));
    lines.push(getEscPosSize('small', useEscPos));
  }

  lines.push('');

  if (data.paymentMethod) {
    const paymentText = `${safeTranslate('Paiement')}: ${safeTranslate(data.paymentMethod === 'cash' ? 'Cash' : 'Card')}`;
    lines.push(alignText(paymentText, 'center', width));
  }

  if (template.footerMessage.enabled && template.footerMessage.text) {
    const textWeight = template.footerMessage.textWeight || 'normal';
    const centerOffset = template.footerMessage.centerOffset || 0;

    lines.push('');

    if (useEscPos) {
      const weightCommands = getTextWeightCommands(textWeight, useEscPos);
      if (weightCommands.start) lines.push(weightCommands.start);
    }

    lines.push(alignText(template.footerMessage.text, template.footerMessage.alignment, width, centerOffset));

    if (useEscPos) {
      const weightCommands = getTextWeightCommands(textWeight, useEscPos);
      if (weightCommands.end) lines.push(weightCommands.end);
    }
  }

  if (useEscPos) {
    lines.push('\x1B\x64\x05');
    lines.push('\x1D\x56\x41\x00');
  }

  return lines.join('\n');
};
